import { DateTime, Interval } from 'luxon';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import './date-picker.scss';
import classNames from 'classnames';
import '@fortawesome/fontawesome-free/css/all.css';
import usePrevious from '../../hooks/use-previous';
import { Calendar } from './calendar/calendar';
import _ from 'lodash';
import { useOnClickOutside } from '../../util/use-on-click-outside';
import Icon from '../icons/icon';
import { Typography } from '../typography/typography';
import { Button } from '../form/button';
import { FieldLabel, FieldLabelProps } from '../field/field';

type DatePickerProps = FieldLabelProps & {
  name?: string;
  value?: DateTime;
  onChange?: (d?: DateTime) => void;
  disabledDaysOfWeek?: number[];
  disabledIntervals?: Interval[];
  highlightedIntervals?: { intervals: Interval; color: string }[];
  placeholder?: string;
  customDisplay?: ReactNode;
  clearable?: boolean;
  forceOpen?: boolean;
  arrowable?: boolean;
  inputClassName?: string;
  inputWrapperClassName?: string;
  alignDropdownRight?: boolean;
  datePickerWrapperClassName?: string;
};

export const DatePicker = ({
  name,
  value,
  onChange,
  disabledDaysOfWeek,
  disabledIntervals,
  highlightedIntervals,
  placeholder,
  customDisplay,
  clearable = false,
  label,
  labelType,
  labelWeight,
  isRequired,
  forceOpen,
  arrowable = false,
  inputClassName,
  inputWrapperClassName,
  alignDropdownRight = false,
  datePickerWrapperClassName,
}: DatePickerProps) => {
  const [inputDateStr, setInputDateStr] = useState<string>('');
  const [error, setError] = useState<string | boolean>();
  const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState<boolean>(false);
  const [didUpdateDateStr, setDidUpdateDateStr] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  const previousValue = usePrevious(value);

  useEffect(() => {
    if (value) {
      if (value.isValid) {
        if (!previousValue || value.startOf('day').diff(previousValue.startOf('day')).milliseconds !== 0) {
          const dateString = value?.toFormat('D') || '';
          setInputDateStr(dateString);
          setDidUpdateDateStr(false);
        }
        const isInDisabledInterval = !!disabledIntervals?.find((i) => i.contains(value));
        if (isInDisabledInterval) {
          setError('The selected time is not available');
        } else {
          setError(undefined);
        }
      }
    } else {
      setInputDateStr('');
      setDidUpdateDateStr(false);
      setError(undefined);
    }
  }, [value, previousValue, disabledIntervals]);

  function tryParseDateTime(inputDateStr: string): DateTime {
    const localesToTry = [Intl.DateTimeFormat().resolvedOptions().locale, undefined, 'en-US'];
    let dateTime = undefined;
    for (let i = 0; i < localesToTry.length; i++) {
      dateTime = DateTime.fromFormat(inputDateStr, 'D', { zone: value?.zoneName, locale: localesToTry[i] });
      if (dateTime.isValid) {
        return dateTime;
      }
    }

    if (dateTime?.isValid) {
      return dateTime;
    } else {
      throw Error('Failed to parse date time');
    }
  }

  const handleInputDateStrUpdate = useRef(
    _.debounce(
      (
        value: DateTime | undefined,
        inputDateStr: string | undefined,
        didUpdateDateStr: boolean,
        clearable: boolean,
      ) => {
        if (didUpdateDateStr) {
          if (inputDateStr) {
            try {
              const dateTime = tryParseDateTime(inputDateStr);
              if (dateTime?.invalidReason) {
                setError('Not a valid date or time');
              } else {
                setError(undefined);
              }
              if (!value || value.startOf('day').diff(dateTime.startOf('day')).milliseconds !== 0) {
                onChange && onChange(dateTime);
              }
              setDidUpdateDateStr(false);
            } catch (e) {
              setError('Not a valid date or time');
            }
          } else if (clearable) {
            setError(undefined);
            onChange && onChange(undefined);
            setDidUpdateDateStr(false);
          }
        }
      },
      300,
    ),
  ).current;

  useEffect(() => {
    handleInputDateStrUpdate(value, inputDateStr, didUpdateDateStr, clearable);
  }, [inputDateStr, didUpdateDateStr, onChange, value, clearable]);

  useOnClickOutside(divRef, () => setIsDateTimePickerOpen(false));

  const onSelectDate = (d?: DateTime) => {
    onChange && onChange(d?.startOf('day'));
    setIsDateTimePickerOpen(false);
  };

  return (
    <div ref={divRef} className={classNames('date-picker-wrapper', datePickerWrapperClassName)}>
      {customDisplay ? (
        <div className="cursor-pointer" onClick={() => setIsDateTimePickerOpen(true)}>
          {customDisplay}
        </div>
      ) : (
        <>
          <FieldLabel label={label} labelType={labelType} labelWeight={labelWeight} isRequired={isRequired} />
          <div className="arrowable">
            {arrowable && (
              <div className="clickable" onClick={() => onChange && onChange(value?.minus({ days: 1 }))}>
                <Icon icon={'ChevronUp'} transform="rotate(-90)" />
              </div>
            )}

            <div
              className={classNames('input-wrapper-date flex justify-between items-center', inputWrapperClassName, {
                focused: isDateTimePickerOpen,
                invalid: error,
              })}
              onClick={() => setIsDateTimePickerOpen(true)}
            >
              <div className="flex items-center ">
                <Icon icon="Calendar" />
                <input
                  className={classNames({ invalid: !!error })}
                  hidden={true}
                  name={name}
                  value={value?.toISO() || ''}
                  readOnly={true}
                />
                <Typography type="text-sm">
                  <input
                    className={classNames({ valid: !!error }, inputClassName)}
                    type="text"
                    value={inputDateStr}
                    placeholder={placeholder}
                    onChange={(e) => {
                      setInputDateStr(e.target.value);
                      setDidUpdateDateStr(true);
                    }}
                  />
                </Typography>
              </div>
              <Button
                className={classNames('button button-tertiary', { invisible: !clearable || !value })}
                disabled={!clearable}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange && onChange(undefined);
                }}
              >
                <Icon icon="X" size="small" title="Clear"></Icon>
              </Button>
            </div>

            {arrowable && (
              <div className="clickable" onClick={() => onChange && onChange(value?.plus({ days: 1 }))}>
                <Icon icon={'ChevronUp'} transform="rotate(90)" />
              </div>
            )}
          </div>
        </>
      )}
      <span className={classNames('error-message', { visible: error })}>{error}</span>

      {(isDateTimePickerOpen || forceOpen) && (
        <div className={`date-picker ${forceOpen ? 'date-picker-inline' : ''} ${alignDropdownRight ? 'right-0' : ''}`}>
          <div className="date-wrapper">
            <Calendar
              value={value}
              disabledDaysOfWeek={disabledDaysOfWeek}
              disabledIntervals={disabledIntervals}
              highlightedIntervals={highlightedIntervals}
              onChange={onSelectDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
